// src/hooks/useUsers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi }   from '../context/ApiProvider';
import { useAuth }  from '../context/AuthProvider';
import type {
  AssignableRole,
  InviteUserRequest,
  InviteUserResponse,
  Role,
  UpdateAgentMeetingPermissionsRequest,
  UpdateAgentTitleRequest,
  UpdateClientAssignmentRequest,
  UpdateUserBusinessDetailsRequest,
  UserRecord,
} from '../api/admin';
import {
  candidateRecordToUserDetails,
  deleteLocalCandidateRecord,
  deleteLocalCandidateRecordsByEmail,
  getCandidateUserRecords,
  getLocalCandidateById,
  isLocalCandidateId,
  updateLocalCandidateRecord,
} from '../lib/careers/registry';

interface Filters {
  q: string;
  role: Role | 'all';
  page: number;
  online?: boolean; // NEW quick filter
  enabled?: boolean;
}

export const USERS_PAGE_SIZE = 20;

/* list users */
export function useUsers(filters: Filters) {
  const api         = useApi();
  const { loading } = useAuth();

  const params = {
    q   : filters.q,
    page: filters.page,
    ...(filters.role !== 'all' && { role: filters.role }),
    ...(filters.online ? { online: true } : {}),
  };

  return useQuery<UserRecord[]>({
    queryKey : ['users', params],
    queryFn  : async () => {
      let remoteUsers: UserRecord[] = [];
      try {
        remoteUsers = await api.get('/admin/users', { params }).then((r) => r.data.users as UserRecord[]);
      } catch (error) {
        if (filters.role !== 'candidate') {
          throw error;
        }
      }
      const candidates = getCandidateUserRecords();
      const normalizedQ = filters.q.trim().toLowerCase();
      const filteredCandidates = candidates.filter((candidate) => {
        if (filters.role !== 'all' && filters.role !== 'candidate') return false;
        if (!normalizedQ) return true;
        const haystack = `${candidate.email} ${candidate.name ?? ''} ${candidate.businessName ?? ''}`.toLowerCase();
        return haystack.includes(normalizedQ);
      });

      if (filters.role === 'candidate') {
        const start = filters.page * USERS_PAGE_SIZE;
        return filteredCandidates.slice(start, start + USERS_PAGE_SIZE);
      }

      if (filters.page !== 0 || filteredCandidates.length === 0) {
        return remoteUsers;
      }

      const seen = new Set<string>();
      const merged: UserRecord[] = [];
      for (const user of remoteUsers) {
        const key = user.email.trim().toLowerCase() || user.id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(user);
      }
      for (const candidate of filteredCandidates) {
        const key = candidate.email.trim().toLowerCase() || candidate.id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(candidate);
      }
      return merged.slice(0, USERS_PAGE_SIZE);
    },
    enabled  : !loading && (filters.enabled ?? true),
    staleTime: 2 * 60 * 1000,
  });
}

/* promote / demote role */
export function useUpdateRole() {
  const api = useApi();
  const qc  = useQueryClient();

  return useMutation({
    mutationFn : ({ uid, role }: { uid: string; role: AssignableRole }) =>
      api.patch(`/admin/users/${uid}/role`, { role }),
    onSuccess  : (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', variables.uid] });
    },
  });
}

/* ban / unban */
export function useUpdateBan() {
  const api = useApi();
  const qc  = useQueryClient();

  return useMutation({
    mutationFn : ({ uid, banned }: { uid: string; banned: boolean }) =>
      api.patch(`/admin/users/${uid}/ban`, { banned }),
    onSuccess  : () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/* create/invite user and send onboarding setup email */
export function useInviteUser() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: InviteUserRequest) =>
      api.post<InviteUserResponse>('/admin/users/invite', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/* delete user */
export function useDeleteUser() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ uid, email }: { uid: string; email?: string | null }) => {
      if (isLocalCandidateId(uid)) {
        const deleted = deleteLocalCandidateRecord(uid);
        if (!deleted) {
          throw new Error('Local candidate record not found.');
        }
        return { ok: true, uid, local: true };
      }
      const response = await api.delete(`/admin/users/${uid}`).then((r) => r.data);
      if (typeof email === 'string' && email.trim().length > 0) {
        deleteLocalCandidateRecordsByEmail(email);
      }
      return response;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.cancelQueries({ queryKey: ['user', variables.uid], exact: true });
      qc.removeQueries({ queryKey: ['user', variables.uid], exact: true });
    },
  });
}

/* update client business details */
export function useUpdateUserBusinessDetails() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ uid, payload }: { uid: string; payload: UpdateUserBusinessDetailsRequest }) => {
      if (isLocalCandidateId(uid)) {
        const local = updateLocalCandidateRecord(uid, {
          firstName: payload.firstName,
          lastName: payload.lastName,
          userType:
            payload.userType === 'business' || payload.userType === 'individual'
              ? payload.userType
              : undefined,
          contactPhone: payload.contactPhone,
          businessName: payload.businessName,
          businessAddress: payload.businessAddress,
          businessWebsite: payload.businessWebsite,
          pointOfContact: payload.pointOfContact,
          bio: payload.bio,
        });
        if (local) return candidateRecordToUserDetails(local);
      }

      return api.patch(`/admin/users/${uid}/details`, payload).then((r) => r.data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', variables.uid] });
    },
  });
}

/* assign/unassign client to agent */
export function useUpdateClientAssignment() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, payload }: { uid: string; payload: UpdateClientAssignmentRequest }) =>
      api.patch(`/admin/users/${uid}/client-assignment`, payload).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', variables.uid] });
      if (typeof variables.payload.agentUid === 'string' && variables.payload.agentUid.trim().length > 0) {
        qc.invalidateQueries({ queryKey: ['user', variables.payload.agentUid] });
      }
    },
  });
}

/* update agent function/title */
export function useUpdateAgentTitle() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, payload }: { uid: string; payload: UpdateAgentTitleRequest }) =>
      api.patch(`/admin/users/${uid}/agent-title`, payload).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', variables.uid] });
      qc.invalidateQueries({ queryKey: ['agent-titles'] });
    },
  });
}

/* update agent meeting permissions */
export function useUpdateAgentMeetingPermissions() {
  const api = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      uid,
      payload,
    }: {
      uid: string;
      payload: UpdateAgentMeetingPermissionsRequest;
    }) =>
      api
        .patch(`/admin/users/${uid}/meeting-permissions`, payload)
        .then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', variables.uid] });
    },
  });
}

/* list available agent titles/functions */
export function useAgentTitles(enabled = true) {
  const api = useApi();
  return useQuery<string[]>({
    queryKey: ['agent-titles'],
    queryFn: () => api.get('/admin/agent-titles').then((r) => r.data.titles as string[]),
    enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/* single user details (for drawer) */
export function useUser(uid: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['user', uid],
    queryFn : async () => {
      if (!uid) return null;
      const local = getLocalCandidateById(uid);
      if (local) return candidateRecordToUserDetails(local);
      try {
        return await api.get(`/admin/users/${uid}`).then((r) => r.data);
      } catch (error) {
        const status = (error as any)?.response?.status;
        // Legacy candidate shadow ids can appear in CRM history datasets.
        // Do not break the drawer with a backend fetch error for these.
        if (
          uid.startsWith('candidate_') ||
          uid.startsWith('career_candidate_')
        ) {
          return {
            id: uid,
            role: 'candidate',
            email: '',
            name: null,
          };
        }
        if (status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled : Boolean(uid),
    staleTime: 60 * 1000,
  });
}

/* export recipients for blast */
export function useExportedRecipients(filters: { q?: string; role?: Role; online?: boolean; ids?: string[] }) {
  const api = useApi();
  return useQuery({
    queryKey: ['users-export', filters],
    queryFn : () => api.get('/admin/users/export', { params: filters }).then(r => r.data),
    enabled : true,
  });
}

/* send mailchimp blast */
export function useSendBlast() {
  const api = useApi();
  return useMutation({
    mutationFn: (payload: {
      filters: { q?: string; role?: Role; online?: boolean; ids?: string[] };
      subject: string;
      preheader?: string;
      html?: string;
      templateId?: number;
      testEmails?: string[];
    }) => api.post('/admin/blasts/mailchimp', payload).then(r => r.data),
  });
}
